import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';

import { isElementOfType } from '../../utilities/components';

import { WpIcon } from '../icon';
import { WpEmoji } from '../emoji';

import './WpLink.scss';

export type Target = '_blank' | '_self' | '_parent' | '_top';

export interface LinkProps extends React.HTMLProps<HTMLAnchorElement> {
  /** The url to link to */
  href: string;
  /** The content to display inside the link */
  children: React.ReactNode;
  /** Where to display the url */
  target?: Target;
  /** Callback when a link is clicked */
  onClick?(): void;
  /** ClassName to be added */
  className?: string;
  /** Makes the browser download the url instead of opening it. */
  download?: string | boolean;
  /** for internal routing using for react-router  */
  state?: { [key: string]: string | number | boolean };
  /** for internal routing for react router dom */
  replace?: boolean;
  [key: string]: any;
}

const WpLink = ({ href, children, target, download, className, state, replace = false, ...rest }: LinkProps) => {
  // if the href starts with '/' it's an internal url
  const isExternal = !href.startsWith('/') && new URL(href).origin !== location.origin;
  const targetToRender = target ? target : isExternal ? '_blank' : null;
  const rel = targetToRender === '_blank' || isExternal ? 'noopener noreferrer' : undefined;

  const childrenLength = Children.count(children);
  const isOnlyIcon = children && childrenLength === 1 && isElementOfType(children, [WpIcon, WpEmoji]);
  const isFirstElementIcon = !isOnlyIcon && children && childrenLength > 1 && isElementOfType(children[0], WpIcon);
  const isLastElementIcon =
    !isOnlyIcon && children && childrenLength > 1 && isElementOfType(children[childrenLength - 1], WpIcon);

  const baseClassName = 'WpLink';
  const classes = classnames(
    baseClassName,
    {
      [`${baseClassName}--icon-start`]: isFirstElementIcon,
      [`${baseClassName}--icon-end`]: isLastElementIcon,
      [`${baseClassName}--icon-only`]: isOnlyIcon,
    },
    className
  );

  if (isExternal) {
    return (
      <a target={targetToRender} {...rest} href={href} rel={rel} className={classes} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <Link to={{ pathname: href, state }} className={classes} {...rest} replace={replace}>
      {children}
    </Link>
  );
};

export default WpLink;
